import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './AdminLayout.css';
import BrandLogo from './BrandLogo';
import AppVersion from './AppVersion';
import { isBranchAdmin, isBrandAdmin, isSuperAdmin, roleLabel } from '../utils/portalRoles';
import usePortalScope from '../hooks/usePortalScope';
import { getAdminNavIcon } from './AdminNavIcons';

const MENU_GROUPS = [
  {
    id: 'overview',
    label: null,
    items: [
      { title: 'Dashboard', path: '/admin/dashboard', menuKey: 'dashboard' }
    ]
  },
  {
    id: 'catalog',
    label: 'Catalog',
    items: [
      {
        title: 'Brands',
        path: '/admin/brands',
        menuKey: 'brands',
        roles: ['superadmin', 'brandadmin']
      },
      { title: 'Branches', path: '/admin/salons', menuKey: 'salons' },
      { title: 'Mirrors', path: '/admin/mirrors', menuKey: 'mirrors' },
      { title: 'Categories', path: '/admin/categories', menuKey: 'categories' },
      { title: 'Services & Pricing', path: '/admin/services', menuKey: 'services' },
      { title: 'Stylists', path: '/admin/stylists', menuKey: 'stylists' }
    ]
  },
  {
    id: 'operations',
    label: 'Operations',
    items: [
      { title: 'Users', path: '/admin/users', menuKey: 'users' },
      { title: 'Transactions', path: '/admin/transactions', menuKey: 'transactions' },
      { title: 'Ongoing Services', path: '/admin/ongoing-services', menuKey: 'ongoing-services' },
      { title: 'Reports', path: '/admin/reports', menuKey: 'reports' },
      { title: 'Feedback', path: '/admin/feedback', menuKey: 'feedback' }
    ]
  },
  {
    id: 'tv',
    label: 'TV content',
    items: [
      { title: 'TV Home Feeds', path: '/admin/feeds', menuKey: 'feeds' },
      { title: 'Promotional Feeds', path: '/admin/promotional-feeds', menuKey: 'promotional-feeds' },
      { title: 'Engagement Services', path: '/admin/engagement-services', menuKey: 'engagement-services' }
    ]
  }
];

const AdminLayout = ({ children, pageTitle, subtitle }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const portal = usePortalScope();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const sidebarNavRef = useRef(null);
  const SIDEBAR_SCROLL_KEY = 'admin_sidebar_scroll_top';

  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);

  const menuGroups = useMemo(() => {
    const role = user?.role;
    return MENU_GROUPS.map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        if (!item.roles) return true;
        if (isSuperAdmin(role)) return true;
        const effective = isBranchAdmin(role) ? 'branchadmin' : role;
        return item.roles.includes(effective) || item.roles.includes(role);
      })
    })).filter((group) => group.items.length > 0);
  }, [user?.role]);

  const flatMenuItems = useMemo(
    () => menuGroups.flatMap((g) => g.items),
    [menuGroups]
  );

  const [activeMenu, setActiveMenu] = useState('dashboard');

  useEffect(() => {
    const currentPath = location.pathname;
    const activeItem = flatMenuItems.find(
      (item) =>
        currentPath === item.path ||
        (item.path !== '/admin/dashboard' && currentPath.startsWith(item.path))
    );
    if (activeItem) {
      setActiveMenu(activeItem.menuKey);
    }
  }, [location.pathname, flatMenuItems]);

  const handleMenuClick = (item) => {
    if (sidebarNavRef.current) {
      sessionStorage.setItem(SIDEBAR_SCROLL_KEY, String(sidebarNavRef.current.scrollTop || 0));
    }
    setActiveMenu(item.menuKey);
    closeMobileNav();
  };

  useEffect(() => {
    closeMobileNav();
  }, [location.pathname, closeMobileNav]);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeMobileNav();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen, closeMobileNav]);

  useEffect(() => {
    const nav = sidebarNavRef.current;
    if (!nav) return;
    const saved = Number(sessionStorage.getItem(SIDEBAR_SCROLL_KEY) || 0);
    nav.scrollTop = Number.isFinite(saved) ? saved : 0;
  }, []);

  useEffect(() => {
    const nav = sidebarNavRef.current;
    if (!nav) return;

    const activeItem = flatMenuItems.find(
      (item) =>
        location.pathname === item.path ||
        (item.path !== '/admin/dashboard' && location.pathname.startsWith(item.path))
    );
    if (!activeItem) return;

    const activeEl = nav.querySelector(`.nav-item[data-menu-key="${activeItem.menuKey}"]`);
    if (!activeEl) return;

    const activeTop = activeEl.offsetTop;
    const activeBottom = activeTop + activeEl.offsetHeight;
    const visibleTop = nav.scrollTop;
    const visibleBottom = nav.scrollTop + nav.clientHeight;

    if (activeTop < visibleTop || activeBottom > visibleBottom) {
      activeEl.scrollIntoView({ block: 'nearest' });
    }
  }, [location.pathname, flatMenuItems]);

  const scopeLabel = (() => {
    if (isBranchAdmin(user?.role) && (portal.salonName || portal.brandName)) {
      return [portal.brandName, portal.salonName].filter(Boolean).join(' · ');
    }
    if (isBrandAdmin(user?.role) && portal.brandName) {
      return portal.brandName;
    }
    return null;
  })();

  return (
    <div className="admin-dashboard-wrapper">
      {mobileNavOpen && (
        <div
          className="admin-sidebar-backdrop"
          role="presentation"
          aria-hidden="true"
          onClick={closeMobileNav}
        />
      )}

      <div className={`admin-sidebar${mobileNavOpen ? ' admin-sidebar--open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <div className="sidebar-brand-logo-wrap">
              <BrandLogo variant="inline" alt="" />
            </div>
            <span className="sidebar-brand-name">Alter Mirror</span>
          </div>
          <button
            type="button"
            className="admin-sidebar-close"
            aria-label="Close navigation menu"
            onClick={closeMobileNav}
          >
            ×
          </button>
        </div>

        <nav className="sidebar-nav" ref={sidebarNavRef} aria-label="Admin">
          {menuGroups.map((group) => (
            <div key={group.id} className="nav-group">
              {group.label && <div className="nav-group-label">{group.label}</div>}
              {group.items.map((item) => (
                <NavLink
                  key={item.menuKey}
                  to={item.path}
                  data-menu-key={item.menuKey}
                  className={({ isActive }) =>
                    `nav-item ${isActive || activeMenu === item.menuKey ? 'active' : ''}`
                  }
                  aria-current={activeMenu === item.menuKey ? 'page' : undefined}
                  onClick={() => handleMenuClick(item)}
                >
                  <span className="nav-icon" aria-hidden="true">
                    {getAdminNavIcon(item.menuKey)}
                  </span>
                  <span className="nav-text">{item.title}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <AppVersion className="sidebar-version" />
        </div>
      </div>

      <div className="admin-main-content">
        <header className="admin-topbar">
          <button
            type="button"
            className="admin-menu-toggle"
            aria-label={mobileNavOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen((o) => !o)}
          >
            <span className="admin-menu-toggle-icon" aria-hidden="true" />
          </button>
          <div className="admin-topbar-titles">
            <h1 className="page-title">{pageTitle || 'Admin'}</h1>
            {subtitle && <p className="page-subtitle">{subtitle}</p>}
          </div>
          <div className="admin-topbar-right">
            <span className="welcome-text">
              {user?.name || 'Admin'}
              {user?.role ? ` · ${roleLabel(user.role)}` : ''}
              {scopeLabel ? ` · ${scopeLabel}` : ''}
            </span>
            <button type="button" className="btn-logout" onClick={logout}>
              Logout
            </button>
          </div>
        </header>

        <div className="admin-content-area">{children}</div>
      </div>
    </div>
  );
};

export default AdminLayout;
